import * as THREE from 'three';

const EDGE_COLOR = 0x000000;
const FACE_EDGES_OVERLAY = 'face-edges-overlay';

export function toggleFaceEdges(objectGroup: THREE.Group): void {
  objectGroup.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;

    const existing = child.children.find(
      (c) => c.userData['overlayType'] === FACE_EDGES_OVERLAY,
    ) as THREE.LineSegments | undefined;

    if (existing) {
      child.remove(existing);
      existing.geometry.dispose();
      (existing.material as THREE.Material).dispose();
      return;
    }

    const lines = buildFaceEdges(child);
    if (lines) child.add(lines);
  });
}

function buildFaceEdges(mesh: THREE.Mesh): THREE.LineSegments | null {
  const geometry = mesh.geometry as THREE.BufferGeometry;
  const topoFaceIds = (
    geometry.userData['faceProperties'] as Map<string, number[]> | undefined
  )?.get('topo');
  if (!topoFaceIds) return null;

  const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
  const positions = posAttr.array as Float32Array;
  const triCount = posAttr.count / 3;
  if (triCount !== topoFaceIds.length) return null;

  // Disconnected PLY guarantees bit-identical float values at shared boundary
  // vertices, so raw float string keys reliably identify shared edges.
  const vKey = (i: number) =>
    `${positions[i * 3]},${positions[i * 3 + 1]},${positions[i * 3 + 2]}`;
  const eKey = (a: number, b: number) => {
    const ka = vKey(a),
      kb = vKey(b);
    return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
  };

  type EdgeEntry = { tris: number[]; a: number; b: number };
  const edgeMap = new Map<string, EdgeEntry>();

  for (let tri = 0; tri < triCount; tri++) {
    const vertices = [tri * 3, tri * 3 + 1, tri * 3 + 2];
    for (const [a, b] of [
      [vertices[0], vertices[1]],
      [vertices[1], vertices[2]],
      [vertices[2], vertices[0]],
    ] as [number, number][]) {
      const key = eKey(a, b);
      const entry = edgeMap.get(key);
      if (entry) entry.tris.push(tri);
      else edgeMap.set(key, { tris: [tri], a, b });
    }
  }

  const vertices: number[] = [];
  for (const { tris, a, b } of edgeMap.values()) {
    if (tris.length > 1 && topoFaceIds[tris[0]] === topoFaceIds[tris[1]])
      continue;
    vertices.push(
      positions[a * 3],
      positions[a * 3 + 1],
      positions[a * 3 + 2],
      positions[b * 3],
      positions[b * 3 + 1],
      positions[b * 3 + 2],
    );
  }

  if (vertices.length === 0) return null;

  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(vertices, 3),
  );

  const mat = new THREE.LineBasicMaterial({
    color: EDGE_COLOR,
  });

  const lines = new THREE.LineSegments(lineGeo, mat);
  lines.userData['overlayType'] = FACE_EDGES_OVERLAY;
  return lines;
}
